/**
 * Product Configurator — DWR-style accordion with fabric preview cards and detail modals.
 *
 * Behaviors:
 * 1. Single-open accordion: closing other steps when one opens
 * 2. Inline preview card: shows fabric image + name when a swatch is selected
 * 3. "View Swatch Details" link opens the matching <basic-modal>
 * 4. Updates selected value text in the step header
 * 5. Re-initializes after section re-render (product-info.js replaces DOM on variant change)
 */
(function () {
  'use strict';

  // Store fabric data globally so it persists across re-inits
  var fabricMapCache = {};

  function initConfigurator() {
    var variantSelects = document.querySelectorAll('variant-selects');
    variantSelects.forEach(function (container) {
      // Skip if already initialized and not re-rendering
      if (!container.dataset.configuratorInit) {
        initVariantSelect(container);
        container.dataset.configuratorInit = 'true';
      }
    });

    // Watch for DOM replacements (product-info.js re-renders sections via AJAX)
    observeSectionReRenders();
  }

  function observeSectionReRenders() {
    var productInfoEls = document.querySelectorAll('product-info');
    productInfoEls.forEach(function (productInfo) {
      if (productInfo.dataset.configuratorObserved) return;
      productInfo.dataset.configuratorObserved = 'true';

      var reinitializing = false;
      var observer = new MutationObserver(function (mutations) {
        if (reinitializing) return;

        // Only re-init if a variant-selects element was actually replaced
        var needsReinit = mutations.some(function (mutation) {
          if (mutation.type !== 'childList') return false;
          for (var i = 0; i < mutation.addedNodes.length; i++) {
            var node = mutation.addedNodes[i];
            if (node.nodeType === 1 && (node.tagName === 'VARIANT-SELECTS' || node.querySelector && node.querySelector('variant-selects'))) {
              return true;
            }
          }
          return false;
        });

        if (needsReinit) {
          reinitializing = true;
          var newContainers = productInfo.querySelectorAll('variant-selects');
          newContainers.forEach(function (container) {
            container.removeAttribute('data-configurator-init');
            initVariantSelect(container);
            container.dataset.configuratorInit = 'true';
          });
          reinitializing = false;
        }
      });

      observer.observe(productInfo, { childList: true, subtree: true });
    });
  }

  function initVariantSelect(container) {
    var steps = container.querySelectorAll('.configurator-step');
    if (!steps.length) return;

    // Parse fabric data if available (cache it for re-inits)
    var sectionId = container.dataset.section || 'default';
    var fabricDataEl = container.querySelector('[data-fabric-data]');
    if (fabricDataEl) {
      try {
        var fabrics = JSON.parse(fabricDataEl.textContent);
        fabricMapCache[sectionId] = {};
        fabrics.forEach(function (f) {
          fabricMapCache[sectionId][f.name] = f;
        });
      } catch (e) {
        console.warn('Configurator: Could not parse fabric data', e);
      }
    }
    var fabricMap = fabricMapCache[sectionId] || {};

    // Single-open accordion behavior
    steps.forEach(function (step) {
      step.addEventListener('toggle', function () {
        if (step.open) {
          steps.forEach(function (other) {
            if (other !== step && other.open) {
              other.removeAttribute('open');
              other.setAttribute('aria-expanded', 'false');
            }
          });
          step.setAttribute('aria-expanded', 'true');
        } else {
          step.setAttribute('aria-expanded', 'false');
        }
      });
    });

    // Listen for swatch/option changes (event delegation on container)
    container.addEventListener('change', function (e) {
      var input = e.target;
      if (input.type !== 'radio') return;

      var selectedValue = input.value;
      var step = input.closest('.configurator-step');
      if (!step) return;

      // Update the selected value text in the step header
      var headerValue = step.querySelector('.configurator-step__selected-value');
      if (headerValue) {
        headerValue.textContent = selectedValue;
      }

      // Show inline preview card if this is a fabric option
      var fabricName = input.getAttribute('data-fabric-name');
      if (fabricName && fabricMap[fabricName]) {
        showPreviewCard(step, fabricMap[fabricName]);
      }
    });

    // Event delegation for preview close buttons and detail links
    container.addEventListener('click', function (e) {
      // Preview card close button
      var closeBtn = e.target.closest('[data-preview-close]');
      if (closeBtn) {
        var preview = closeBtn.closest('.configurator-preview');
        if (preview) preview.classList.remove('is-visible');
        return;
      }

      // "View Swatch Details" link
      var detailLink = e.target.closest('[data-preview-link]');
      if (detailLink) {
        var fabricId = detailLink.getAttribute('data-modal-target');
        if (fabricId) {
          openFabricModal(fabricId);
        }
        return;
      }

      // Collection info (?) button
      var infoBtn = e.target.closest('[data-collection-info]');
      if (infoBtn) {
        var collName = infoBtn.getAttribute('data-collection-info');
        for (var name in fabricMap) {
          if (fabricMap[name].collection === collName) {
            openFabricModal(fabricMap[name].id);
            break;
          }
        }
        return;
      }
    });

    // Initialize preview for already-selected fabric on page load
    steps.forEach(function (step) {
      var checked = step.querySelector('input[type="radio"][data-fabric-name]:checked');
      if (checked) {
        var fabricName = checked.getAttribute('data-fabric-name');
        if (fabricName && fabricMap[fabricName]) {
          showPreviewCard(step, fabricMap[fabricName]);
        }
      }
    });
  }

  function openFabricModal(fabricId) {
    var modal = document.getElementById('FabricModal-' + fabricId);
    if (modal && typeof modal.show === 'function') {
      modal.show();
    } else if (modal) {
      // Fallback if .show() isn't available
      modal.setAttribute('open', '');
    }
  }

  function showPreviewCard(step, fabric) {
    var preview = step.querySelector('.configurator-preview');
    if (!preview || !fabric) return;

    var img = preview.querySelector('[data-preview-image]');
    var name = preview.querySelector('[data-preview-name]');
    var link = preview.querySelector('[data-preview-link]');

    if (img && fabric.image_small) {
      img.src = fabric.image_small;
      img.alt = fabric.name || '';
    }
    if (name) {
      name.textContent = fabric.name || '';
    }
    if (link && fabric.id) {
      link.setAttribute('data-modal-target', fabric.id);
    }

    preview.classList.add('is-visible');
  }

  /* ===== Swatch Request Modal ===== */

  var CA_PROVINCES = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
    'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'
  ];
  var US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
  ];

  function initSwatchRequestModal() {
    var modals = document.querySelectorAll('[id^="SwatchRequestModal"]');
    modals.forEach(function (modal) {
      var checkboxes = modal.querySelectorAll('[data-swatch-check]');
      var countEl = modal.querySelector('[data-swatch-count]');
      var countrySelect = modal.querySelector('[data-swatch-country]');
      var stateSelect = modal.querySelector('[data-swatch-state]');
      var form = modal.querySelector('form');
      var hiddenField = modal.querySelector('[data-selected-swatches-field]');
      var MAX_SWATCHES = 5;

      // Checkbox limit enforcement
      function updateCheckboxState() {
        var checked = modal.querySelectorAll('[data-swatch-check]:checked');
        var checkedCount = checked.length;

        if (countEl) {
          countEl.textContent = checkedCount + ' of ' + MAX_SWATCHES + ' selected';
        }

        checkboxes.forEach(function (cb) {
          if (!cb.checked) {
            cb.disabled = checkedCount >= MAX_SWATCHES;
          }
        });

        // Update hidden field with selected swatch names
        if (hiddenField) {
          var names = [];
          checked.forEach(function (cb) { names.push(cb.value); });
          hiddenField.value = names.join(', ');
        }
      }

      checkboxes.forEach(function (cb) {
        cb.addEventListener('change', updateCheckboxState);
      });

      // Country / State dynamic switching
      function populateStates(country) {
        if (!stateSelect) return;
        var list = country === 'CA' ? CA_PROVINCES : country === 'US' ? US_STATES : [];
        stateSelect.innerHTML = '<option value="">Choose a State/Province</option>';
        list.forEach(function (name) {
          var opt = document.createElement('option');
          opt.value = name;
          opt.textContent = name;
          stateSelect.appendChild(opt);
        });
      }

      if (countrySelect) {
        // Populate on init (default is CA)
        populateStates(countrySelect.value);
        countrySelect.addEventListener('change', function () {
          populateStates(this.value);
        });
      }

      // Validate at least 1 swatch selected on submit
      if (form) {
        form.addEventListener('submit', function (e) {
          // Remove any existing error notification
          var existing = modal.querySelector('.swatch-request__error');
          if (existing) existing.remove();

          var checked = modal.querySelectorAll('[data-swatch-check]:checked');
          if (checked.length === 0) {
            e.preventDefault();
            e.stopImmediatePropagation();

            // Show inline notification
            var notice = document.createElement('div');
            notice.className = 'swatch-request__error';
            notice.textContent = 'Please select at least one swatch.';
            var swatchSection = modal.querySelector('.swatch-request__swatches');
            if (swatchSection) {
              swatchSection.appendChild(notice);
              notice.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            return false;
          }
        });
      }
    });

    // Open modal from the "Request Free Swatches" button (delegated to survive re-renders)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.configurator-preview__request-btn');
      if (!btn) return;
      var modalId = btn.getAttribute('aria-controls');
      var modal = document.getElementById(modalId);
      if (modal && typeof modal.show === 'function') {
        modal.show(btn, true);
      } else if (modal) {
        modal.setAttribute('open', '');
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initConfigurator();
      initSwatchRequestModal();
    });
  } else {
    initConfigurator();
    initSwatchRequestModal();
  }
})();
